import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Printer, X, RotateCw } from "lucide-react";
import { PressPass, TERM_LABELS } from "./types";

interface PassPrintViewProps {
  pass: PressPass;
  onClose: () => void;
  /** Org branding, when the caller has it separately (org-side issuer flow).
   *  Falls back to pass.organization when omitted (portal viewer flow). */
  organization?: PressPass["organization"];
  /** Only institution staff may print the card — journalists can only view/flip it. */
  canPrint?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  active: "#008244",
  expired: "#6b7280",
  revoked: "#dc2626",
};

export function PassPrintView({ pass, onClose, organization, canPrint = false }: PassPrintViewProps) {
  const [flipped, setFlipped] = useState(false);
  const journalist = pass.journalist;
  const org = organization ?? pass.organization ?? null;

  const fullName = journalist
    ? `${journalist.first_name} ${journalist.last_name}`
    : "";
  const nationality = journalist?.nationality ?? "";
  const initials = journalist
    ? `${journalist.first_name?.[0] ?? ""}${journalist.last_name?.[0] ?? ""}`.toUpperCase()
    : "?";

  const validFrom = new Date(pass.valid_from).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const validTo = new Date(pass.valid_to).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const isExpired = new Date(pass.valid_to) < new Date();
  const effectiveStatus = pass.status === "active" && isExpired ? "expired" : pass.status;
  const statusLabel = effectiveStatus.toUpperCase();
  const statusColor = STATUS_COLORS[effectiveStatus] ?? "#6b7280";

  const primary = org?.brand_primary || "#0D2240";
  const accent = org?.brand_accent || "#008244";
  const orgName = org?.name || "Accredited Institution";

  // qr_payload is a relative link to the public accreditation-check page
  // (e.g. "/org-slug/accreditation-check?verify=..."); resolve it to an
  // absolute, scannable URL. Older/malformed payloads that aren't a path
  // are rendered as-is rather than silently dropped.
  const qrValue = pass.qr_payload
    ? pass.qr_payload.startsWith("/")
      ? `${window.location.origin}${pass.qr_payload}`
      : pass.qr_payload
    : null;

  useEffect(() => {
    document.title = `Press Pass: ${pass.pass_number}`;
    return () => {
      document.title = "CONORBIT Media";
    };
  }, [pass.pass_number]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      {/* Screen controls, hidden when printing */}
      <div className="print:hidden absolute top-4 right-4 flex gap-2">
        <Button variant="outline" size="sm" onClick={() => setFlipped(f => !f)} className="bg-white">
          <RotateCw className="w-4 h-4 mr-1" />
          Flip
        </Button>
        {canPrint && (
          <Button variant="outline" size="sm" onClick={() => window.print()} className="bg-white">
            <Printer className="w-4 h-4 mr-1" />
            Print
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onClose} className="bg-white">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Card, flip stage */}
      <div
        id="press-pass-card"
        className="relative"
        style={{ width: 340, height: 530, perspective: 1200, fontFamily: "Arial, sans-serif" }}
      >
        <button
          type="button"
          aria-label="Flip card to see the other side"
          onClick={() => setFlipped(f => !f)}
          className="print:hidden absolute inset-0 w-full h-full cursor-pointer"
          style={{
            transformStyle: "preserve-3d",
            transition: "transform 0.6s",
            transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
          }}
        >
          {/* FRONT */}
          <div
            className="absolute inset-0 rounded-2xl overflow-hidden shadow-2xl bg-white"
            style={{ backfaceVisibility: "hidden" }}
          >
            <div className="px-4 py-3 text-white flex items-center gap-2.5" style={{ backgroundColor: primary }}>
              {org?.logo_url && (
                <img src={org.logo_url} alt="" className="w-8 h-8 rounded object-contain bg-white/90 p-0.5 shrink-0" />
              )}
              <div className="min-w-0">
                <div className="text-[11px] font-bold tracking-wide uppercase truncate">{orgName}</div>
                <div className="text-[10px] opacity-80 uppercase tracking-widest">Press / Media Accreditation</div>
              </div>
            </div>

            <div className="flex flex-col items-center pt-5 pb-3 px-4">
              {journalist?.photo_url ? (
                <img
                  src={journalist.photo_url}
                  alt={fullName}
                  className="w-24 h-24 rounded-full object-cover border-4"
                  style={{ borderColor: accent }}
                />
              ) : (
                <div
                  className="w-24 h-24 rounded-full flex items-center justify-center text-white text-2xl font-bold border-4"
                  style={{ backgroundColor: primary, borderColor: accent }}
                >
                  {initials}
                </div>
              )}

              <p className="text-[17px] font-bold leading-tight text-center mt-3" style={{ color: primary }}>
                {fullName}
              </p>
              {journalist?.position && (
                <p className="text-[11px] text-gray-600 text-center mt-0.5">{journalist.position}</p>
              )}
              {pass.outlet_name && (
                <p className="text-[11px] font-medium text-gray-700 text-center">{pass.outlet_name}</p>
              )}
              {nationality && <p className="text-[10px] text-gray-400 mt-0.5">{nationality}</p>}
            </div>

            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[9.5px] text-gray-500 uppercase tracking-wide">Pass No.</span>
                <span className="font-mono font-bold text-[12px]" style={{ color: accent }}>{pass.pass_number}</span>
              </div>
              {journalist?.verification_id && (
                <div className="flex items-center justify-between">
                  <span className="text-[9.5px] text-gray-500 uppercase tracking-wide">Verification ID</span>
                  <span className="font-mono text-[11px] text-gray-700">{journalist.verification_id}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-[9.5px] text-gray-500 uppercase tracking-wide">Valid</span>
                <span className="text-[10.5px] font-medium" style={{ color: isExpired ? "#dc2626" : "inherit" }}>
                  {validFrom} → {validTo}
                </span>
              </div>
              <div className="flex items-center justify-between pt-0.5">
                {pass.accreditation_term ? (
                  <span className="text-[9.5px] font-medium text-gray-500 capitalize">
                    {TERM_LABELS[pass.accreditation_term] ?? pass.accreditation_term}
                  </span>
                ) : <span />}
                <span
                  className="text-[9.5px] font-bold px-2 py-0.5 rounded-full text-white"
                  style={{ backgroundColor: statusColor }}
                >
                  {statusLabel}
                </span>
              </div>
            </div>
          </div>

          {/* BACK */}
          <div
            className="absolute inset-0 rounded-2xl overflow-hidden shadow-2xl bg-white flex flex-col"
            style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
          >
            <div className="px-4 py-3 text-white text-center" style={{ backgroundColor: primary }}>
              <div className="text-[12px] font-bold tracking-wide uppercase">{orgName}</div>
            </div>

            <div className="flex-1 flex flex-col items-center justify-center px-5 py-4 gap-2">
              {qrValue ? (
                <QRCodeSVG value={qrValue} size={110} level="M" />
              ) : (
                <div className="w-[110px] h-[110px] bg-gray-100 flex items-center justify-center text-[10px] text-gray-400">
                  No QR
                </div>
              )}
              <p className="text-[9.5px] text-gray-500 text-center">Scan to verify accreditation status</p>
            </div>

            <div className="px-5 pb-4 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-center">
                <div>
                  <div className="border-b border-gray-300 h-8" />
                  <p className="text-[8.5px] text-gray-500 mt-1">Signature of Holder</p>
                </div>
                <div>
                  <div className="border-b border-gray-300 h-8" />
                  <p className="text-[8.5px] text-gray-500 mt-1">Signature of Issuing Officer</p>
                </div>
              </div>
              <p className="text-[8.5px] text-gray-500 text-center leading-snug">
                This card is the property of {orgName}. If found, please return it to the issuing institution's
                communications office.
              </p>
            </div>
          </div>
        </button>

        {/* Print-only static rendering: two physical faces stacked, no 3D transform */}
        <div className="hidden print:block">
          <div className="rounded-2xl overflow-hidden bg-white" style={{ width: 340 }}>
            <div className="px-4 py-3 text-white flex items-center gap-2.5" style={{ backgroundColor: primary }}>
              {org?.logo_url && (
                <img src={org.logo_url} alt="" className="w-8 h-8 rounded object-contain bg-white/90 p-0.5 shrink-0" />
              )}
              <div className="min-w-0">
                <div className="text-[11px] font-bold tracking-wide uppercase truncate">{orgName}</div>
                <div className="text-[10px] opacity-80 uppercase tracking-widest">Press / Media Accreditation</div>
              </div>
            </div>
            <div className="flex flex-col items-center pt-5 pb-3 px-4">
              {journalist?.photo_url ? (
                <img src={journalist.photo_url} alt={fullName} className="w-24 h-24 rounded-full object-cover border-4" style={{ borderColor: accent }} />
              ) : (
                <div className="w-24 h-24 rounded-full flex items-center justify-center text-white text-2xl font-bold border-4" style={{ backgroundColor: primary, borderColor: accent }}>
                  {initials}
                </div>
              )}
              <p className="text-[17px] font-bold leading-tight text-center mt-3" style={{ color: primary }}>{fullName}</p>
              {journalist?.position && <p className="text-[11px] text-gray-600 text-center mt-0.5">{journalist.position}</p>}
              {pass.outlet_name && <p className="text-[11px] font-medium text-gray-700 text-center">{pass.outlet_name}</p>}
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[9.5px] text-gray-500 uppercase tracking-wide">Pass No.</span>
                <span className="font-mono font-bold text-[12px]" style={{ color: accent }}>{pass.pass_number}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[9.5px] text-gray-500 uppercase tracking-wide">Valid</span>
                <span className="text-[10.5px] font-medium">{validFrom} → {validTo}</span>
              </div>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden bg-white mt-4" style={{ width: 340 }}>
            <div className="px-4 py-3 text-white text-center" style={{ backgroundColor: primary }}>
              <div className="text-[12px] font-bold tracking-wide uppercase">{orgName}</div>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center px-5 py-4 gap-2">
              {qrValue && <QRCodeSVG value={qrValue} size={110} level="M" />}
              <p className="text-[9.5px] text-gray-500 text-center">Scan to verify accreditation status</p>
            </div>
            <div className="px-5 pb-4 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-center">
                <div><div className="border-b border-gray-300 h-8" /><p className="text-[8.5px] text-gray-500 mt-1">Signature of Holder</p></div>
                <div><div className="border-b border-gray-300 h-8" /><p className="text-[8.5px] text-gray-500 mt-1">Signature of Issuing Officer</p></div>
              </div>
              <p className="text-[8.5px] text-gray-500 text-center leading-snug">
                This card is the property of {orgName}. If found, please return it to the issuing institution's communications office.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Print-only styles */}
      <style>{`
        @media print {
          body > *:not(#press-pass-card) { display: none !important; }
          #press-pass-card {
            position: fixed !important;
            top: 0; left: 0;
            height: auto !important;
            box-shadow: none !important;
          }
        }
      `}</style>
    </div>
  );
}
